import { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────────
   Styles — BEM prefix: pf__
───────────────────────────────────────────────────────────────────────────── */
const css = `
  /* Trigger button */
  .pf__trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: "Outfit", sans-serif;
    font-size: 13px;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 10px;
    border: 1.5px solid #e8e8e8;
    background: transparent;
    color: #444444;
    cursor: pointer;
    transition: all 0.25s ease;
    white-space: nowrap;
  }
  .pf__trigger:hover {
    border-color: #1971c2;
    color: #1971c2;
    background: rgba(25,113,194,0.05);
  }

  /* Overlay */
  .pf__overlay {
    position: fixed;
    inset: 0;
    background: rgba(8,8,8,0.45);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: pf-fade-in 0.2s ease;
  }
  @keyframes pf-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* Modal panel */
  .pf__panel {
    background: #ffffff;
    border-radius: 20px;
    border: 1px solid #ebebeb;
    width: 100%;
    max-width: 640px;
    box-shadow: 0 20px 60px rgba(8,8,8,0.12);
    animation: pf-slide-up 0.25s ease;
    overflow: hidden;
  }
  @keyframes pf-slide-up {
    from { transform: translateY(16px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  /* Header */
  .pf__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid #ebebeb;
  }
  .pf__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .pf__header-icon {
    color: #1971c2;
  }
  .pf__title {
    font-family: "Outfit", sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #080808;
    margin: 0;
  }
  .pf__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1.5px solid #e8e8e8;
    background: transparent;
    color: #888888;
    cursor: pointer;
    transition: all 0.25s ease;
  }
  .pf__close:hover {
    border-color: #1971c2;
    color: #1971c2;
    background: rgba(25,113,194,0.05);
  }

  /* Body */
  .pf__body {
    padding: 24px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  /* Select fields */
  .pf__select {
    font-family: "Outfit", sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #444444;
    background: #f4f4f4;
    border: 1.5px solid #e4e4e4;
    border-radius: 10px;
    padding: 10px 14px;
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 36px;
    cursor: pointer;
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .pf__select:focus {
    outline: none;
    border-color: #1971c2;
    box-shadow: 0 0 0 3px rgba(25,113,194,0.1);
    background-color: #ffffff;
  }
  .pf__select option {
    color: #444444;
  }

  /* Last select spans full width when odd count */
  .pf__select-wrap--full {
    grid-column: 1 / -1;
  }

  /* Footer */
  .pf__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid #ebebeb;
  }
  .pf__btn-reset {
    font-family: "Outfit", sans-serif;
    font-size: 13px;
    font-weight: 600;
    padding: 9px 20px;
    border-radius: 10px;
    border: 1.5px solid #e8e8e8;
    background: transparent;
    color: #444444;
    cursor: pointer;
    transition: all 0.25s ease;
  }
  .pf__btn-reset:hover {
    border-color: #080808;
    color: #080808;
  }
  .pf__btn-apply {
    font-family: "Outfit", sans-serif;
    font-size: 13px;
    font-weight: 600;
    padding: 9px 22px;
    border-radius: 10px;
    border: none;
    background: #1971c2;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.25s ease, transform 0.25s ease;
  }
  .pf__btn-apply:hover {
    background: #1864ab;
    transform: translateY(-1px);
  }

  /* Responsive */
  @media (max-width: 560px) {
    .pf__body {
      grid-template-columns: 1fr;
    }
    .pf__select-wrap--full {
      grid-column: 1;
    }
  }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */

const EMPTY_FILTERS = {
  purpose: "",
  state: "",
  city: "",
  area: "",
  type: "",
  subType: "",
  subSubType: "",
};

const SelectField = ({ name, value, onChange, placeholder, options, full }) => (
  <div className={full ? "pf__select-wrap--full" : undefined}>
    <select
      className="pf__select"
      name={name}
      value={value}
      onChange={onChange}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.id} value={opt.id}>
          {opt.name}
        </option>
      ))}
    </select>
  </div>
);

const PropertyFilter = ({
  purposes = [],
  states = [],
  cities = [],
  areas = [],
  types = [],
  subTypes = [],
  subSubTypes = [],
  onFilter,
}) => {
  const [show, setShow] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const handleChange = (e) =>
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleApply = () => {
    onFilter(filters);
    setShow(false);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    onFilter({});
    setShow(false);
  };

  const closeOnOverlay = (e) => {
    if (e.target === e.currentTarget) setShow(false);
  };

  return (
    <>
      {/* Inject scoped styles once */}
      <style>{css}</style>

      {/* Trigger */}
      <button
        type="button"
        className="pf__trigger"
        onClick={() => setShow(true)}
        title="Filter Properties"
      >
        <SlidersHorizontal size={14} />
        Filter
      </button>

      {/* Modal */}
      {show && (
        <div className="pf__overlay" onClick={closeOnOverlay}>
          <div className="pf__panel" role="dialog" aria-modal="true" aria-label="Filter Properties">
            {/* Header */}
            <div className="pf__header">
              <div className="pf__header-left">
                <SlidersHorizontal size={18} className="pf__header-icon" />
                <h2 className="pf__title">Filter Properties</h2>
              </div>
              <button
                type="button"
                className="pf__close"
                onClick={() => setShow(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="pf__body">
              <SelectField name="purpose"    value={filters.purpose}    onChange={handleChange} placeholder="All Purposes"       options={purposes}    />
              <SelectField name="state"      value={filters.state}      onChange={handleChange} placeholder="All States"         options={states}      />
              <SelectField name="city"       value={filters.city}       onChange={handleChange} placeholder="All Cities"         options={cities}      />
              <SelectField name="area"       value={filters.area}       onChange={handleChange} placeholder="All Areas"          options={areas}       />
              <SelectField name="type"       value={filters.type}       onChange={handleChange} placeholder="All Types"          options={types}       />
              <SelectField name="subType"    value={filters.subType}    onChange={handleChange} placeholder="All Sub Types"      options={subTypes}    />
              <SelectField name="subSubType" value={filters.subSubType} onChange={handleChange} placeholder="All Sub Sub Types"  options={subSubTypes} full />
            </div>

            {/* Footer */}
            <div className="pf__footer">
              <button type="button" className="pf__btn-reset" onClick={handleReset}>
                Reset
              </button>
              <button type="button" className="pf__btn-apply" onClick={handleApply}>
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PropertyFilter;